import React, { FC, useCallback, useContext, useState } from 'react';

import { IdeaFormContext } from 'buyside/components/presentation/IdeaForm/ideaForm';
import AnonymousField from 'buyside/components/presentation/IdeaForm/Disclaimers/DisclaimersFields/anonymousField';
import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import CheckboxField from 'buyside/components/presentation/Fields/checkboxField';
import ContestField from 'buyside/components/presentation/IdeaForm/Disclaimers/DisclaimersFields/contestField';
import EmployerField from 'buyside/components/presentation/ResearchFormWizard/SharedFields/employerField';
import FundProfileDisplayField from 'buyside/components/presentation/ResearchFormWizard/SharedFields/fundProfileDisplayField';
import Modal from 'buyside/components/presentation/Modal/modal';
import ParsedHtml from 'buyside/components/presentation/ParsedHtml/parsedHtml';
import PositionDisclaimerFundField from 'buyside/components/presentation/ResearchFormWizard/SharedFields/positionDisclaimerFundField';
import ResearchFormWizardSection from 'buyside/components/presentation/ResearchFormWizard/researchFormWizardSection';
import RequiredDisclaimerFields from 'buyside/components/presentation/ResearchFormWizard/SharedFields/requiredDisclaimerFields';

import { DISCLAIMERS } from 'buyside/util/researchSubmission';

import styles from './disclaimersStyles.scss';

const Disclaimers: FC = () => {
  const [isTermsOfUseModalOpen, setIsTermsOfUseModalOpen] = useState(false);

  const { currentContests, currentUser, isDraft, termsOfUse } = useContext(IdeaFormContext);

  if (!currentUser || !termsOfUse) throw new Error();

  const { id: currentUserId, currentEmployers } = currentUser;

  const renderOpenTermsOfUseModalButton = useCallback(
    () => (
      <Button
        className={styles.termsOfUseButton}
        onClick={() => setIsTermsOfUseModalOpen(true)}
        size={BUTTON_SIZES.SMALL}
        text="SumZero Terms of Use"
        type={BUTTON_TYPES.LINK}
      />
    ),
    []
  );

  const renderTermsModal = useCallback(
    () => (
      <Modal
        isOpen={isTermsOfUseModalOpen}
        onClose={() => setIsTermsOfUseModalOpen(false)}
        renderOpenModalButton={renderOpenTermsOfUseModalButton}
        size="xl"
        title="SumZero - Terms of Use"
      >
        <div className={styles.termsOfUseModal}>
          <ParsedHtml html={termsOfUse}>{({ content }) => <>{content}</>}</ParsedHtml>
          <p>
            <strong>Note: </strong> The terms of use are separate for each distinct product on
            SumZero. Customers using the SumZero Basic product must read and agree to the SumZero
            Basic Terms of Use. Customers using SumZero Elite must read and agree to the SumZero
            Elite Terms of Use. Be sure to review the terms governing the product pertinent to your
            use of the Site.
          </p>
        </div>
      </Modal>
    ),
    [isTermsOfUseModalOpen, renderOpenTermsOfUseModalButton, setIsTermsOfUseModalOpen, termsOfUse]
  );

  return (
    <>
      <ResearchFormWizardSection title="Optional Disclosures and Visibility on Site">
        <CheckboxField
          label={DISCLAIMERS.OPTIONAL.positionDisclaimerIndividual}
          name="positionDisclaimerIndividual"
          size="small"
        />
        <AnonymousField currentUserId={currentUserId} />
        {isDraft ? <ContestField currentContests={currentContests} /> : null}
        {currentEmployers.length ? (
          <>
            <EmployerField currentEmployers={currentEmployers} isDraft={isDraft} />
            <PositionDisclaimerFundField label={DISCLAIMERS.OPTIONAL.positionDisclaimerFund} />
            <FundProfileDisplayField currentEmployers={currentEmployers} researchType="Idea" />
          </>
        ) : null}
        <small>Research posted anonymously is excluded from monthly Member Rankings.</small>
      </ResearchFormWizardSection>
      <ResearchFormWizardSection title="Required Legal Disclosures">
        <RequiredDisclaimerFields />
        <small>
          Please refer to the {renderTermsModal()} for additional provisions you are agreeing to
          abide by when you contribute content to the SumZero Research Database.
        </small>
      </ResearchFormWizardSection>
    </>
  );
};

export default Disclaimers;
